import { checkLibraryDuplicate } from "@/api/library-documents/library-documents";
import { Modal } from "@/components/shared/Modal";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    DocumentPickerValue,
    UploadTabSelection,
} from "./DocumentPicker.types";
import { ModalFileDropzone } from "./ModalFileDropzone";

interface DocumentUploadModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (value: DocumentPickerValue) => void;
}

export const DocumentUploadModal = ({
    open,
    onClose,
    onConfirm,
}: DocumentUploadModalProps) => {
    // ModalFileDropzone manages internal state (localFile, upload mutation) that
    // persists across Dialog close/open because MUI keeps children mounted.
    // Conditionally rendering on `open` ensures a fresh mount each time.
    const renderDropzone = open;

    const [uploadSelection, setUploadSelection] =
        useState<UploadTabSelection>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const [confirmError, setConfirmError] = useState<string | null>(null);

    const resetState = useCallback(() => {
        setUploadSelection(null);
        setIsUploading(false);
        setIsConfirming(false);
        setConfirmError(null);
    }, []);

    const handleConfirm = useCallback(async () => {
        if (!uploadSelection) return;
        setIsConfirming(true);
        setConfirmError(null);
        try {
            const dupResult = await checkLibraryDuplicate(
                uploadSelection.objectName
            );
            if (dupResult.match) {
                if (dupResult.match.is_knowledge_base) {
                    setConfirmError(
                        "This document is in the Knowledge Base. Move it out of the Knowledge Base if you want to use it for scopes."
                    );
                    setIsConfirming(false);
                    return;
                }
                const value: DocumentPickerValue = {
                    source: "library",
                    objectName: dupResult.match.object_name,
                    libraryDocumentId: dupResult.match.document_id,
                    title: dupResult.match.document_name,
                    folderId: dupResult.match.library_folder_id,
                    folderName: dupResult.match.folder_name,
                };
                resetState();
                onConfirm(value);
                return;
            }
            const value: DocumentPickerValue = {
                source: "upload",
                objectName: uploadSelection.objectName,
                title: uploadSelection.file.name,
                byteSize: uploadSelection.byteSize,
            };
            resetState();
            onConfirm(value);
        } catch {
            setConfirmError(
                "Failed to check for duplicates. Please try again."
            );
            setIsConfirming(false);
        }
    }, [uploadSelection, resetState, onConfirm]);

    // Auto-confirm when upload completes. Ref avoids stale closure — the
    // effect should only re-fire when `uploadSelection` changes, not when
    // `handleConfirm` is recreated.
    const handleConfirmRef = useRef(handleConfirm);
    handleConfirmRef.current = handleConfirm;
    const isConfirmingRef = useRef(false);
    isConfirmingRef.current = isConfirming;

    useEffect(() => {
        if (uploadSelection && !isConfirmingRef.current) {
            handleConfirmRef.current();
        }
    }, [uploadSelection]);

    const handleSelectionChange = useCallback((sel: UploadTabSelection) => {
        setUploadSelection(sel);
        setConfirmError(null);
    }, []);

    const handleClose = useCallback(() => {
        if (isConfirming) return;
        resetState();
        onClose();
    }, [isConfirming, resetState, onClose]);

    const actionLabel = isConfirming
        ? "Loading..."
        : isUploading
          ? "Uploading..."
          : "Upload";

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Upload Document"
            subtitle="Document will be saved to your library"
            maxWidth="sm"
            showCancel
            isLoading={isConfirming}
            error={confirmError}
            actions={[
                {
                    label: actionLabel,
                    onClick: handleConfirm,
                    disabled: !uploadSelection || isConfirming || isUploading,
                },
            ]}
        >
            {renderDropzone && (
                <ModalFileDropzone
                    selection={uploadSelection}
                    onSelectionChange={handleSelectionChange}
                    onUploadingChange={setIsUploading}
                />
            )}
        </Modal>
    );
};
